import {
  configureStep,
  expandStep,
  findStep,
  getUnorderedSteps,
  loadCompletedLogs,
  selectPermalinkLine,
  toggleGroup,
  toggleAction,
} from './steps'
import {fromEvent} from '@github-ui/subscription'
import {loadCheckRunLogs} from './check-runs'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'

let permalinkStepNumber: number | null = null
let permalinkLine: number | null = null
let isPermalinkFromAnnotation: boolean = false

function parseLocationHash() {
  let permalinkInfo = window.location.hash.match(/^#step:(\d+):(\d+)$/) || []
  if (permalinkInfo.length === 0) {
    permalinkInfo = window.location.hash.match(/^#annotation:(\d+):(\d+)$/) || []
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    permalinkStepNumber = +permalinkInfo[1]! || null
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    permalinkLine = +permalinkInfo[2]! || null
    isPermalinkFromAnnotation = true
  } else {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    permalinkStepNumber = +permalinkInfo[1]! || null
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    permalinkLine = +permalinkInfo[2]! || null
    isPermalinkFromAnnotation = false
  }
}

parseLocationHash()

on('change', '.js-checks-log-timestamps', event => {
  const timestamps = event.currentTarget as HTMLInputElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const logsContainer = timestamps.closest<HTMLElement>('.js-check-steps')!
  logsContainer.classList.toggle('CheckRun-show-timestamps', timestamps.checked)
})

observe('.js-checks-log-details', {
  async add(container) {
    const checkSteps = document.querySelector<HTMLElement>('.js-steps-blankslate')
    if (checkSteps) checkSteps.hidden = true
    const checkRunLogs = document.querySelector<HTMLElement>('.js-check-run-log-display')
    if (checkRunLogs) checkRunLogs.hidden = true
    const search = document.querySelector<HTMLElement>('.js-check-run-search')
    if (search) search.hidden = false
    const step = configureStep(container as HTMLElement)
    const expand = container.getAttribute('data-expand') && permalinkStepNumber === null
    const hasInlineDetails =
      container.getAttribute('data-step-type') === 'wait' || container.getAttribute('data-step-type') === 'wait-all'
    const plink = step.number === permalinkStepNumber
    if (expand && hasInlineDetails) {
      expandStep(step)
    }
    if (plink || expand) {
      if (!hasInlineDetails) {
        await loadCompletedLogs(step, {keepLoader: true, scrollToBottom: !plink, permaLink: plink})
      }
      if (plink && permalinkLine !== null) {
        selectPermalinkLine(step, permalinkLine, true)
      }
    }
  },
})

observe('.js-check-run-blankslate', {
  subscribe: el =>
    fromEvent(el, 'socket:message', function (event: Event) {
      const target = event.target as Element
      const {has_steps: hasSteps} = (event as CustomEvent).detail.data
      if (hasSteps) target.remove()
    }),
})

observe('.js-steps-dropdown', {
  subscribe: el =>
    fromEvent(el, 'socket:message', function (event: Event) {
      const target = event.target as Element
      const {data} = (event as CustomEvent).detail
      const {log_archive: logArchive, raw_logs: rawLogs} = data
      if (logArchive) {
        const item = target.querySelector<HTMLElement>('.js-steps-dropdown-log-archive')
        if (item) item.hidden = false
      }
      if (rawLogs) {
        const item = target.querySelector<HTMLElement>('.js-steps-dropdown-raw-logs')
        if (item) item.hidden = false
      }
      if (logArchive || rawLogs) {
        const divider = target.querySelector<HTMLElement>('.js-steps-dropdown-divider')
        if (divider) divider.hidden = false
      }
    }),
})

observe('.js-check-run-log-display', {
  async add(container) {
    await loadCheckRunLogs(container as HTMLElement)
  },
})

if (ResizeObserver) {
  const setLogTitleStickyValues = (checkRunHeaderElem: HTMLElement) => {
    const style = window.getComputedStyle(checkRunHeaderElem)
    const {borderBottomWidth, marginBottom} = style
    const logTitles = document.getElementsByClassName('uxr_CheckStep-header') as HTMLCollectionOf<HTMLElement>
    for (const logTitle of logTitles) {
      const newTop = checkRunHeaderElem.clientHeight + parseInt(marginBottom, 10) + parseInt(borderBottomWidth, 10)
      logTitle.style.top = `${newTop}px`
    }
  }

  const observer = new ResizeObserver(entries => {
    for (const entry of entries) {
      setLogTitleStickyValues(entry.target as HTMLElement)
    }
  })

  // observe the check run header for size changes
  // and adjust the `top` attribute of the log title accordingly
  observe('.js-checks-log-toolbar', {
    constructor: HTMLElement,
    add(el) {
      setLogTitleStickyValues(el)
      // start observing the check run header
      observer.observe(el)
    },
    remove(el) {
      // remove the observer
      observer.unobserve(el)
    },
  })
}

on('click', '.js-steps-dropdown-raw-logs', event => {
  event.currentTarget.closest('details')?.removeAttribute('open')
})

// When a grouped log line is expanded we need to programmatically show or hide the lines
// and remember the index for the next time the lines are rendered
on(
  'toggle',
  '.js-checks-log-group',
  event => {
    const details = event.target as Element
    const step = findStep(details)
    if (!step) return

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const parentLine = details.closest<HTMLElement>('.CheckStep-line')!
    const lineElement = parentLine.parentElement
    if (!lineElement) return

    const index = Number(lineElement.getAttribute('data-index') || '0')
    toggleGroup(step, index, details.hasAttribute('open'))
  },
  {capture: true},
)

// Handle expand/collapse of composite action bars in the log view
on(
  'toggle',
  '.js-checks-action-region',
  event => {
    const details = event.target as Element
    const step = findStep(details)
    if (!step) return

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const parentLine = details.closest<HTMLElement>('.CheckStep-line')!
    const lineElement = parentLine.parentElement
    if (!lineElement) return

    const index = Number(lineElement.getAttribute('data-index') || '0')
    toggleAction(step, index, details.hasAttribute('open'))
  },
  {capture: true},
)

async function locationHashChanged() {
  parseLocationHash()
  if (permalinkLine === null) return

  const step = getUnorderedSteps().find(stp => stp.number === permalinkStepNumber)
  if (step) {
    await loadCompletedLogs(step, {keepLoader: true})

    // (true) If URL changes by clicking an annotation than we want to auto-expand any steps and scroll to the location
    // (false) If the URL changes because the user clicks on one of the line numbers than we don't want to auto-scroll
    selectPermalinkLine(step, permalinkLine, isPermalinkFromAnnotation)
  }
}

window.addEventListener('hashchange', locationHashChanged, false)
